// 누적 현황

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { MoreHorizontal } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'
import Chart from '@/components/data/Chart'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { fetchTaskSummaryMonth } from '@/store/taskSummarySlice'
import DepartmentDetailPanel from './components/DepartmentDetailPanel'
import BusinessDetailPanel from './components/BusinessDetailPanel'
import TaskItemsListPanel from '../DetailDashboard/components/TaskItemsListPanel'
import TasksListPanel from '../DetailDashboard/components/TasksListPanel'
import TaskBudgetsListPanel from '../DetailDashboard/components/TaskBudgetsListPanel'
import TaskPerformancesListPanel from '../DetailDashboard/components/TaskPerformancesListPanel'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import TaskItemCreatePanel from '@/pages/project/TaskManagement/components/TaskItemCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import { useTaskEditFromReport } from '@/hooks/useTaskEditFromReport'
import { useShowTasks } from '@/hooks/useShowTasks'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import {
  TaskSummaryMonthlyByBusinessResponse,
  TaskSummaryMonthlyByDepartmentResponse,
  TaskSummaryService,
  TaskReportService,
} from '@/api'

type ListType = 'work' | 'task' | 'budget' | 'performance'
type SummaryListPanelType = 'task_item' | 'task' | 'budget' | 'performance'

export default function OverallDashboardPage() {
  // Redux hooks
  const dispatch = useAppDispatch()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 요약 카드·탭을 숨긴다
  const showTasks = useShowTasks()
  const { summary: taskSummary, monthSummary: taskSummaryMonth, isLoading: isSummaryLoading } = useAppSelector(state => state.taskSummary)

  // 전체 뷰로 고정 (기간별 탭 제거)

  // List view state for 전체 tab
  const [selectedListType, setSelectedListType] = useState<'work' | 'task' | 'budget' | 'performance'>('work')

  // 세부실행과제 표시가 꺼지면 선택돼 있던 세부실행과제 탭에서 수행업무 탭으로 옮긴다
  useEffect(() => {
    if (!showTasks && selectedListType === 'task') {
      setSelectedListType('work')
    }
  }, [showTasks, selectedListType])

  // Department/Business summary state
  const [summaryViewType, setSummaryViewType] = useState<'department' | 'business'>('business')
  const [departmentSummary, setDepartmentSummary] = useState<TaskSummaryMonthlyByDepartmentResponse | null>(null)
  const [businessSummary, setBusinessSummary] = useState<TaskSummaryMonthlyByBusinessResponse | null>(null)
  const [isDeptBusinessSummaryLoading, setIsDeptBusinessSummaryLoading] = useState(false)
  const [deptBusinessSummaryError, setDeptBusinessSummaryError] = useState<string | null>(null)

  // Department detail side panel state
  const [isDepartmentDetailPanelOpen, setIsDepartmentDetailPanelOpen] = useState(false)
  const [selectedDepartmentName, setSelectedDepartmentName] = useState('')
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | undefined>(undefined)

  // Business detail side panel state
  const [isBusinessDetailPanelOpen, setIsBusinessDetailPanelOpen] = useState(false)
  const [selectedBusinessName, setSelectedBusinessName] = useState('')
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | undefined>(undefined)

  // 차트/요약 클릭 → 항목별 리스트 사이드패널 (한 번에 하나만 열림)
  // filterBusinessId/filterDepartmentId: 부서/사업별 섹션의 차트 클릭 시 그 엔티티로 좁힘
  // dateRangeOverride: 월별 봉/포인트를 정확히 클릭한 경우 해당 월(YYYY-MM-01 ~ 말일)로 좁힘
  const [summaryListPanel, setSummaryListPanel] = useState<{
    type: SummaryListPanelType
    title: string
    filterBusinessId?: string
    filterBusinessName?: string
    filterDepartmentId?: string
    filterDepartmentName?: string
    dateRangeOverride?: { start: Date; end: Date }
  } | null>(null)

  // 목록 패널에서 행 클릭 → 항목 상세 사이드패널 (z-index가 list 패널보다 위)
  const [isTaskDetailPanelOpen, setIsTaskDetailPanelOpen] = useState(false)
  const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState<string | null>(null)
  const [isTaskItemDetailPanelOpen, setIsTaskItemDetailPanelOpen] = useState(false)
  const [selectedTaskItemIdForDetail, setSelectedTaskItemIdForDetail] = useState<string | null>(null)
  const [isBudgetDetailPanelOpen, setIsBudgetDetailPanelOpen] = useState(false)
  const [selectedBudgetTaskIdForDetail, setSelectedBudgetTaskIdForDetail] = useState<string | null>(null)
  const [isPerformanceDetailPanelOpen, setIsPerformanceDetailPanelOpen] = useState(false)
  const [selectedPerformanceTaskIdForDetail, setSelectedPerformanceTaskIdForDetail] = useState<string | null>(null)

  const handleTaskRowClick = useCallback((task: TaskTreeNode) => {
    if (!task?.id) return
    setSelectedTaskIdForDetail(task.id)
    setIsTaskDetailPanelOpen(true)
  }, [])
  const handleTaskItemRowClick = useCallback((taskItem: TaskTreeNode) => {
    if (!taskItem?.id) return
    setSelectedTaskItemIdForDetail(taskItem.id)
    setIsTaskItemDetailPanelOpen(true)
  }, [])
  const handleBudgetItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedBudgetTaskIdForDetail(taskId)
    setIsBudgetDetailPanelOpen(true)
  }, [])
  const handlePerformanceItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedPerformanceTaskIdForDetail(taskId)
    setIsPerformanceDetailPanelOpen(true)
  }, [])

  // ===== 상세 패널 "수정" 클릭 → 수정 사이드패널 (DetailDashboard와 동일 패턴) =====
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [editTaskData, setEditTaskData] = useState<{
    id: string
    name: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    assigneeId: string
    assigneeName: string
    subAssigneeId: string
    subAssigneeName: string
    managerId: string
    managerName: string
    reviewerId: string
    reviewerName: string
    departmentId: string
    departmentName: string
    startAt: string
    endAt: string
    description: string
    labels?: Array<{ id: string; business_label_id: string; key: string }>
    detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
    extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    /** AI 분석 프롬프트에 함께 실리는 판단근거 */
    judgmentBasis?: string
    /** 편집 패널의 budget/performance 초기값에 사용 (raw originalData) */
    taskOriginal?: any
  } | null>(null)
  const [isTaskItemEditPanelOpen, setIsTaskItemEditPanelOpen] = useState(false)
  const [editTaskItemData, setEditTaskItemData] = useState<{
    id: string
    name: string
    taskId: string
    taskName: string
    taskSubgroupName: string
    businessName: string
    startAt: string
    endAt: string
    output: string
  } | null>(null)
  // 수정 성공 시 증가 → 상세 패널이 자체 재조회
  const [taskDetailRefreshKey, setTaskDetailRefreshKey] = useState(0)
  // 수정 성공 시 증가 → 세부실행과제 목록 패널 재조회
  const [tasksListRefreshTrigger, setTasksListRefreshTrigger] = useState(0)

  const handleTaskEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task' || !task.originalData) return
    const taskData = task.originalData as any
    setEditTaskData({
      id: task.id,
      name: task.name,
      taskSubgroupId: taskData.task_subgroup_id || '',
      taskSubgroupName: taskData.task_subgroup_name || '',
      taskGroupName: taskData.task_group_name || '',
      businessName: task.business_name || '',
      businessId: taskData.business_id || '',
      assigneeId: taskData.assignee?.id || '',
      assigneeName: taskData.assignee?.name || '',
      subAssigneeId: taskData.sub_assignee?.id || '',
      subAssigneeName: taskData.sub_assignee?.name || '',
      managerId: taskData.manager?.id || '',
      managerName: taskData.manager?.name || '',
      reviewerId: taskData.reviewer?.id || '',
      reviewerName: taskData.reviewer?.name || '',
      departmentId: taskData.department_id || '',
      departmentName: task.department_name || '',
      startAt: taskData.start_at || '',
      endAt: taskData.end_at || '',
      description: taskData.description || '',
      labels: taskData.labels || [],
      detailInfos: taskData.detail_infos || [],
      extraInfos: taskData.extra_infos || [],
      judgmentBasis: taskData.judgment_basis || '',
      taskOriginal: taskData
    })
    setIsTaskEditPanelOpen(true)
    // 상세 패널은 열린 상태 유지 — 수정 패널이 위(z)에 뜬다
  }, [])

  const handleTaskItemEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task_item' || !task.originalData) return
    const taskItemData = task.originalData as any
    setEditTaskItemData({
      id: task.id,
      name: task.name,
      taskId: taskItemData.task_id || '',
      taskName: taskItemData.task_name || '',
      taskSubgroupName: taskItemData.task_subgroup_name || '',
      businessName: task.business_name || '',
      startAt: taskItemData.start_at || '',
      endAt: taskItemData.end_at || '',
      output: taskItemData.output || ''
    })
    setIsTaskItemEditPanelOpen(true)
  }, [])

  const handleTaskEditPanelClose = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
  }, [])

  const handleTaskEditSuccess = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
    // 상세 패널 재조회 + 열려 있는 세부실행과제 목록 패널 재조회
    setTaskDetailRefreshKey(k => k + 1)
    setTasksListRefreshTrigger(t => t + 1)
  }, [])

  const handleTaskItemEditPanelClose = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
  }, [])

  const handleTaskItemEditSuccess = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
    setTaskDetailRefreshKey(k => k + 1)
  }, [])

  // 액션 버튼(보고서) 클릭 시 AG Grid의 row click이 함께 발화해 detail이 열리는 문제 차단용 플래그.
  // TasksTable의 handleRowClick은 이 플래그가 true면 onTaskClick 호출을 스킵한다.
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // 보고서 상세 패널 — 패널이 자체 fetch하므로 task + selectedTemplate만 보관
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null,
  })
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  const fetchReportTemplates = useCallback(async (workspaceId: string, businessId?: string | null) => {
    if (!workspaceId) return null
    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)
    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId,
        businessId: businessId || undefined,
      })
      setReportTemplates(response || [])
      return response || []
    } catch (error: any) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿 목록을 불러오는데 실패했습니다.')
      return null
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }, [])

  const handleReportClick = useCallback(async (task: TaskTreeNode) => {
    // 행 클릭이 함께 발화하지 않도록 플래그 셋 (TasksTable.handleRowClick에서 체크)
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    // 혹시 이미 열려 있던 detail 패널은 닫는다 (중복 표시 방지)
    setIsTaskDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)

    if (selectedWorkspace?.id) {
      const businessId = (task.originalData as any)?.business_id || (task as any).business_id
      const templates = await fetchReportTemplates(selectedWorkspace.id, businessId)
      if (templates && templates.length > 0) {
        const firstTemplate = templates[0]
        setReportPanelState({
          isOpen: true,
          selectedTask: task,
          selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
        })
      } else {
        setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
      }
    } else {
      setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
    }
  }, [selectedWorkspace?.id, fetchReportTemplates])

  const handleReportDetailPanelClose = useCallback(() => {
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
  }, [])

  // 보고서 패널의 "세부실행과제 수정" → 보고서 패널을 닫고 수정 패널을 연다. 저장 후 목록 재조회
  const handleReportTaskEditSuccess = useCallback(() => {
    setTasksListRefreshTrigger(t => t + 1)
  }, [])
  const { openTaskEdit: handleReportTaskEdit, taskEditPanel: reportTaskEditPanel } = useTaskEditFromReport({
    onBeforeOpen: handleReportDetailPanelClose,
    onSuccess: handleReportTaskEditSuccess,
  })

  const handleReportTemplateChange = useCallback((templateKey: string, templateName: string) => {
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: templateKey ? { key: templateKey, name: templateName } : null,
    }))
  }, [])

  // 보고서 패널에서 "과제 보기" 클릭 → 보고서 패널 닫고 세부실행과제 상세 패널 열기
  const handleReportTaskDetail = useCallback(() => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask?.id) return
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
    setSelectedTaskIdForDetail(selectedTask.id)
    setIsTaskDetailPanelOpen(true)
  }, [reportPanelState.selectedTask])

  // ApexCharts 차트 영역 클릭(`chart.events.click` 또는 `dataPointSelection`) 후, 외곽 div onClick으로
  // bubble되어 컨테이너 핸들러가 월별/봉 핸들러를 덮어쓰는 문제 방지용 가드 ref.
  const chartPointClickGuardRef = React.useRef<number>(0)

  // ApexCharts는 옵션 객체가 새로 생성돼도 이벤트 핸들러를 항상 재바인딩하지 않아
  // 초기 렌더 시점의 stale closure를 들고 있을 수 있다. 이를 우회하기 위해 각 차트별
  // "최신 click 핸들러"를 ref로 보관하고, ApexCharts 이벤트는 항상 ref.current를 호출만 한다.
  const monthlyTrendChartHandlerRef = React.useRef<((idx: number) => void) | null>(null)
  // 부서/사업별 섹션의 월별 차트 — 엔티티별 stable ref가 필요해 Map으로 관리
  const entityChartHandlerRefMapRef = React.useRef<
    Map<string, React.MutableRefObject<((idx: number) => void) | null>>
  >(new Map())

  const getOrCreateEntityChartHandlerRef = useCallback((key: string) => {
    const map = entityChartHandlerRefMapRef.current
    let r = map.get(key)
    if (!r) {
      r = { current: null }
      map.set(key, r)
    }
    return r
  }, [])

  // listType → panel type / 표시 이름 매핑
  const listTypeToPanelType = useMemo<Record<ListType, SummaryListPanelType>>(() => ({
    work: 'task_item',
    task: 'task',
    budget: 'budget',
    performance: 'performance',
  }), [])

  const itemNameByListType = useMemo<Record<ListType, string>>(() => ({
    work: '수행업무',
    task: '세부실행과제',
    budget: '예산',
    performance: '성과',
  }), [])

  const closeSummaryListPanel = useCallback(() => setSummaryListPanel(null), [])

  // 엔티티 옵션 → 패널 state로 변환. entityName은 filterBusinessId/filterDepartmentId 중 set된 쪽의 라벨이다.
  const buildEntityFields = (opts?: { filterBusinessId?: string; filterDepartmentId?: string; entityName?: string }) => ({
    filterBusinessId: opts?.filterBusinessId,
    filterBusinessName: opts?.filterBusinessId ? opts?.entityName : undefined,
    filterDepartmentId: opts?.filterDepartmentId,
    filterDepartmentName: opts?.filterDepartmentId ? opts?.entityName : undefined,
  })

  // 차트 컨테이너(외곽 div) 클릭 → 전체 목록
  const handleChartContainerClick = useCallback((
    listType: ListType,
    options?: { filterBusinessId?: string; filterDepartmentId?: string; entityName?: string },
  ) => {
    const now = Date.now()
    if (now - chartPointClickGuardRef.current < 300) {
      return
    }
    const type = listTypeToPanelType[listType]
    const entityPrefix = options?.entityName ? `${options.entityName} ` : '전체 '
    setSummaryListPanel({
      type,
      title: `${entityPrefix}${itemNameByListType[listType]} 목록`,
      ...buildEntityFields(options),
    })
  }, [listTypeToPanelType, itemNameByListType])

  // 월별 봉/포인트 클릭 → 해당 월로 좁힘
  const handleChartPointClick = useCallback((
    listType: ListType,
    monthKey: string | undefined,
    options?: { filterBusinessId?: string; filterDepartmentId?: string; entityName?: string },
  ) => {
    chartPointClickGuardRef.current = Date.now()
    const type = listTypeToPanelType[listType]
    const entityPrefix = options?.entityName ? `${options.entityName} ` : '전체 '

    if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) {
      setSummaryListPanel({
        type,
        title: `${entityPrefix}${itemNameByListType[listType]} 목록`,
        ...buildEntityFields(options),
      })
      return
    }
    const [yearStr, monthStr] = monthKey.split('-')
    const year = parseInt(yearStr, 10)
    const monthIdx = parseInt(monthStr, 10) - 1
    const start = new Date(year, monthIdx, 1)
    const end = new Date(year, monthIdx + 1, 1)
    end.setMilliseconds(end.getMilliseconds() - 1)
    setSummaryListPanel({
      type,
      title: `${entityPrefix}${itemNameByListType[listType]} 목록 (${year}년 ${monthIdx + 1}월)`,
      ...buildEntityFields(options),
      dateRangeOverride: { start, end },
    })
  }, [listTypeToPanelType, itemNameByListType])

  // ApexCharts click → handler ref에 들어갈 함수 빌더
  const buildChartClickHandler = useCallback((
    listType: ListType,
    monthKeys: string[],
    options?: { filterBusinessId?: string; filterDepartmentId?: string; entityName?: string },
  ) => {
    return (idx: number) => {
      // 차트 영역 클릭은 항상 가드를 세워 상위 컨테이너 핸들러가 뒤이어 실행되지 않게 한다.
      chartPointClickGuardRef.current = Date.now()
      // 월별 봉/포인트 또는 x축 월 라벨을 정확히 클릭했을 때만 해당 월 목록을 연다.
      // 그 외 차트 빈 영역 클릭은 무시한다 — 기간 필터 없는 목록이 열리면 사용자가 오류로 인지한다.
      if (idx >= 0 && monthKeys[idx]) {
        handleChartPointClick(listType, monthKeys[idx], options)
      }
    }
  }, [handleChartPointClick])

  // Handle more options button click
  // const handleMoreOptions = () => {
  //   console.warn('더보기 기능 미구현')
  // }

  // Handle list type change for 전체 tab
  const handleListTypeChange = (listType: 'work' | 'task' | 'budget' | 'performance') => {
    setSelectedListType(listType)
  }

  // Handle summary view type change
  const handleSummaryViewTypeChange = (viewType: 'department' | 'business') => {
    setSummaryViewType(viewType)
  }

  // Handle department detail panel
  const handleOpenDepartmentDetail = (departmentName: string, businessId?: string) => {
    setSelectedDepartmentName(departmentName)
    setSelectedBusinessId(businessId)
    setIsDepartmentDetailPanelOpen(true)
  }

  const handleCloseDepartmentDetail = () => {
    setIsDepartmentDetailPanelOpen(false)
    setSelectedDepartmentName('')
    setSelectedBusinessId(undefined)
  }

  // Handle business detail panel
  const handleOpenBusinessDetail = (businessName: string, departmentId?: string) => {
    setSelectedBusinessName(businessName)
    setSelectedDepartmentId(departmentId)
    setIsBusinessDetailPanelOpen(true)
  }

  const handleCloseBusinessDetail = () => {
    setIsBusinessDetailPanelOpen(false)
    setSelectedBusinessName('')
    setSelectedDepartmentId(undefined)
  }

  // Get monthly trend title based on selected list type
  const getMonthlyTrendTitle = () => {
    switch (selectedListType) {
      case 'task':
        return '전체 월별 세부실행과제 진행율'
      case 'work':
        return '전체 월별 수행업무 진행율'
      case 'budget':
        return '전체 월별 예산 집행율'
      case 'performance':
        return '전체 월별 성과 달성율'
      default:
        return '-'
    }
  }

  // Get summary section title based on selected summary view type and list type
  const getSummaryTitle = () => {
    const viewTypeName = summaryViewType === 'department' ? '부서' : '사업'
    const summaryTypeName = (() => {
      switch (selectedListType) {
        case 'task': return '세부실행과제'
        case 'work': return '수행업무'
        case 'budget': return '예산'
        case 'performance': return '성과'
        default: return '세부실행과제'
      }
    })()
    return `${viewTypeName} 별 ${summaryTypeName} 현황`
  }

  // Fetch department/business summary data
  const fetchDepartmentBusinessSummary = useCallback(async () => {
    if (!selectedWorkspace?.id) {
      setDeptBusinessSummaryError('워크스페이스가 선택되지 않았습니다.')
      return
    }

    setIsDeptBusinessSummaryLoading(true)
    setDeptBusinessSummaryError(null)

    try {
      const filters = {
        workspaceId: selectedWorkspace.id,
      }

      // Fetch both department and business summaries in parallel
      const [deptResponse, businessResponse] = await Promise.all([
        TaskSummaryService.appsTaskViewsGetTaskSummaryMonthlyByDepartment(filters),
        TaskSummaryService.appsTaskViewsGetTaskSummaryMonthlyByBusiness(filters)
      ])

      setDepartmentSummary(deptResponse)
      setBusinessSummary(businessResponse)
    } catch (err) {
      console.error('Failed to fetch department/business summary:', err)
      setDeptBusinessSummaryError('부서/사업별 요약 정보를 불러오는데 실패했습니다.')
    } finally {
      setIsDeptBusinessSummaryLoading(false)
    }
  }, [selectedWorkspace?.id])

  // 전체 뷰 데이터 로드 (월별 요약 API) - fetchDepartmentBusinessSummary를 dependency에서 제거
  useEffect(() => {
    if (selectedWorkspace?.id) {
      const filters = {
        workspaceId: selectedWorkspace.id,
      }
      dispatch(fetchTaskSummaryMonth(filters))
    }
  }, [selectedWorkspace?.id, dispatch])

  // Department/Business summary 별도 로드 - 한 번만 실행되도록 보장
  useEffect(() => {
    fetchDepartmentBusinessSummary()
  }, [fetchDepartmentBusinessSummary])

  // 전체 뷰 요약 차트 데이터 준비
  const summaryChartData = useMemo(() => {
    if (taskSummaryMonth) {
      return [
        {
          listType: 'work' as ListType,
          title: '수행업무',
          value: taskSummaryMonth.task_item.total.count_percentage,
          total: taskSummaryMonth.task_item.total.total_count,
          completed: taskSummaryMonth.task_item.total.completed_count,
          color: designTokens.colors.secondary.dark,
          label: '진행율',
        },
        {
          listType: 'task' as ListType,
          title: '세부실행과제',
          value: taskSummaryMonth.task.total.count_percentage,
          total: taskSummaryMonth.task.total.total_count,
          completed: taskSummaryMonth.task.total.completed_count,
          color: designTokens.colors.primary.dark,
          label: '진행율',
        },
        {
          listType: 'budget' as ListType,
          title: '예산',
          value: taskSummaryMonth.task_budget.total.figure_percentage,
          total: taskSummaryMonth.task_budget.total.total_target_figure,
          completed: taskSummaryMonth.task_budget.total.total_done_figure,
          color: designTokens.colors.warning,
          label: '집행율',
          isAmount: true,
        },
        {
          listType: 'performance' as ListType,
          title: '성과',
          value: taskSummaryMonth.task_performance.total.count_percentage,
          total: taskSummaryMonth.task_performance.total.total_count,
          completed: taskSummaryMonth.task_performance.total.completed_count,
          color: designTokens.colors.success,
          label: '달성율',
        },
      ].filter(item => showTasks || item.listType !== 'task')
    }
    return null
  }, [taskSummaryMonth, showTasks])
  // Chart data transformation functions for monthly data
  const transformMonthlyDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '완료', type: 'column', data: [] },
          { name: '완료율', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      const totalCount = typeof monthlyData[month]?.total_count === 'number' ? monthlyData[month]?.total_count : 0
      const completedCount = typeof monthlyData[month]?.completed_count === 'number' ? monthlyData[month]?.completed_count : 0

      return `${year}년 ${parseInt(monthNum)}월 (${totalCount}/${completedCount})`
    })
    // Safely extract data with fallback values
    const totalData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_count
      return typeof value === 'number' ? value : 0
    })
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '완료',
          type: 'column',
          data: completedData
        },
        {
          name: '완료율',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task item data
  const transformMonthlyTaskItemDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '완료', type: 'column', data: [] },
          { name: '완료율', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      const totalCount = typeof monthlyData[month]?.total_count === 'number' ? monthlyData[month]?.total_count : 0
      const completedCount = typeof monthlyData[month]?.completed_count === 'number' ? monthlyData[month]?.completed_count : 0

      return `${year}년 ${parseInt(monthNum)}월 (${totalCount}/${completedCount})`
    })

    // Safely extract data with fallback values
    const totalData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_count
      return typeof value === 'number' ? value : 0
    })
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '완료',
          type: 'column',
          data: completedData
        },
        {
          name: '완료율',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task budget data
  const transformMonthlyTaskBudgetDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '집행', type: 'column', data: [] },
          { name: '집행률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const totalData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_target_figure
      return typeof value === 'number' ? value : 0
    })
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_done_figure
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.figure_percentage
      return typeof value === 'number' ? value : 0
    })

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '집행',
          type: 'column',
          data: completedData
        },
        {
          name: '집행률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart data transformation for monthly task performance data
  const transformMonthlyTaskPerformanceDataToChart = useCallback((monthlyData: Record<string, any>) => {
    // Validate input data
    if (!monthlyData || typeof monthlyData !== 'object') {
      return {
        categories: [],
        monthKeys: [] as string[],
        series: [
          { name: '전체', type: 'column', data: [] },
          { name: '달성', type: 'column', data: [] },
          { name: '달성률', type: 'line', data: [] }
        ]
      }
    }

    // Sort months chronologically
    const sortedMonths = Object.keys(monthlyData).sort()

    // Transform data for ApexCharts
    const categories = sortedMonths.map(month => {
      // Convert YYYY-MM to more readable format
      const [year, monthNum] = month.split('-')
      return `${year}년 ${parseInt(monthNum)}월`
    })

    // Safely extract data with fallback values
    const totalData = sortedMonths.map(month => {
      const value = monthlyData[month]?.total_count
      return typeof value === 'number' ? value : 0
    })
    const completedData = sortedMonths.map(month => {
      const value = monthlyData[month]?.completed_count
      return typeof value === 'number' ? value : 0
    })
    const percentageData = sortedMonths.map(month => {
      const value = monthlyData[month]?.count_percentage
      return typeof value === 'number' ? value : 0
    })

    return {
      categories,
      monthKeys: sortedMonths,
      series: [
        {
          name: '전체',
          type: 'column',
          data: totalData
        },
        {
          name: '달성',
          type: 'column',
          data: completedData
        },
        {
          name: '달성률',
          type: 'line',
          data: percentageData
        }
      ]
    }
  }, [])

  // Chart options for stacked bar + line chart
  const getChartOptions = useCallback((
    categories: string[],
    chartType: 'task' | 'work' | 'budget' | 'performance' = 'task',
    seriesData?: any[],
    handlerRef?: React.MutableRefObject<((dataPointIndex: number) => void) | null>,
  ) => {
    // Get chart type specific configurations
    const getChartConfig = () => {
      switch (chartType) {
        case 'task':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'work':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '수행업무 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
        case 'budget':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '집행',
            thirdSeriesName: '집행률',
            yAxisTitle: '예산 금액',
            percentageTitle: '집행률 (%)',
            unit: '원'
          }
        case 'performance':
          return {
            firstSeriesName: '전체',
            secondSeriesName: '달성',
            thirdSeriesName: '달성률',
            yAxisTitle: '성과 과제 수',
            percentageTitle: '달성률 (%)',
            unit: '개'
          }
        default:
          return {
            firstSeriesName: '전체',
            secondSeriesName: '완료',
            thirdSeriesName: '완료율',
            yAxisTitle: '세부실행과제 수',
            percentageTitle: '완료율 (%)',
            unit: '개'
          }
      }
    }

    const config = getChartConfig()

    // Calculate max value from both column series to sync Y-axis scales
    let sharedMax: number | undefined
    if (seriesData && seriesData.length >= 2) {
      const series0Data = seriesData[0]?.data || []
      const series1Data = seriesData[1]?.data || []
      const max0 = Math.max(...series0Data, 0)
      const max1 = Math.max(...series1Data, 0)
      const rawMax = Math.max(max0, max1)
      // Round up to nearest 10 (e.g., 93 -> 100, 141 -> 150)
      sharedMax = Math.ceil(rawMax / 100) * 100
    }

    return {
      chart: {
        type: 'line' as const,
        stacked: false,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        events: {
          // 차트 SVG 영역 어디든 클릭하면 발화. dataPointIndex === -1(봉/포인트가 아닌 영역)이면
          // 핸들러가 목록을 열지 않고 가드만 세운다. x축 월 라벨은 아래 xAxisLabelClick이 처리한다.
          click: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            handlerRef?.current?.(idx)
          },
          // 봉/마커를 정확히 클릭했을 때만 발화 — 안전망으로 함께 둔다.
          dataPointSelection: (event: any, _chartContext: any, config: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof config?.dataPointIndex === 'number' ? config.dataPointIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
          // x축 월 라벨 클릭 → 봉/포인트 클릭과 동일하게 해당 월로 좁힌 목록을 연다.
          // labelIndex는 categories 인덱스이고 monthKeys와 1:1로 대응한다.
          xAxisLabelClick: (event: any, _chartContext: any, opts: any) => {
            try { event?.stopPropagation?.() } catch { /* noop */ }
            const idx = typeof opts?.labelIndex === 'number' ? opts.labelIndex : -1
            if (idx >= 0) handlerRef?.current?.(idx)
          },
        },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
        },
      },
      stroke: {
        width: [0, 0, 3],
        curve: 'smooth' as const,
      },
      xaxis: {
        categories: categories,
        title: {
          text: '월별',
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '14px',
            color: designTokens.colors.gray[5],
          },
        },
        labels: {
          style: {
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: '12px',
            colors: designTokens.colors.gray[4],
            // 월 라벨도 클릭하면 해당 월 목록이 열리므로 클릭 가능 커서를 표시한다 (globals.css)
            cssClass: 'dot-clickable-xaxis-label',
          },
        },
      },
      yaxis: [
        {
          seriesName: config.firstSeriesName,
          title: {
            text: config.yAxisTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => {
              if (chartType === 'budget') {
                return `${Math.round(value).toLocaleString()}원`
              }
              return `${value.toLocaleString()}${config.unit}`
            },
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.secondSeriesName,
          show: false,
          ...(sharedMax !== undefined && { max: sharedMax, min: 0, stepSize:10, tickAmount:5, forceNiceScale: true }),
        },
        {
          seriesName: config.thirdSeriesName,
          opposite: true,
          title: {
            text: config.percentageTitle,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '14px',
              color: designTokens.colors.gray[5],
            },
          },
          labels: {
            formatter: (value: number) => `${value.toFixed(1)}%`,
            style: {
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '12px',
              colors: designTokens.colors.gray[4],
            },
          },
          ...({ max: 100, min: 0, stepSize:10, tickAmount:10, forceNiceScale: true }),
        },
      ],
      colors: [designTokens.colors.gray[3], designTokens.colors.primary.dark, designTokens.colors.warning.dark],
      legend: {
        fontFamily: designTokens.typography.fontFamily.sans[0],
        fontSize: '12px',
        position: 'top' as const,
        horizontalAlign: 'center' as const,
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: '12px',
        },
        y: {
          formatter: (value: number, { seriesIndex }: any) => {
            if (seriesIndex === 2) {
              return `${value.toFixed(1)}%`
            }
            if (chartType === 'budget') {
              return `${Math.round(value).toLocaleString()}원`
            }
            return `${value.toLocaleString()}${config.unit}`
          },
        },
      },
      grid: {
        borderColor: designTokens.colors.gray[2],
      },
    }
  }, [])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Summary Section - For both Period and All Views */}
      <div className="flex-shrink-0">
          {/* Summary Title */}
          <h2
            className="mb-4"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: designTokens.typography.fontSize['title-small'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
              color: designTokens.colors.gray[6],
            }}
          >
            요약
          </h2>
          <div
            className="bg-white rounded-lg border p-4"
            style={{ borderColor: designTokens.colors.gray[1] }}
          >


            {/* Summary Charts */}
            {isSummaryLoading ? (
              <div className="flex items-center justify-center h-48">
                <span style={{ color: designTokens.colors.gray[4] }}>
                  요약 정보를 불러오는 중...
                </span>
              </div>
            ) : summaryChartData ? (
              <div className="flex items-stretch gap-5">
                {summaryChartData.map((item, index) => {
                  // 세부실행과제 카드가 빠질 수 있어 인덱스 대신 항목에 실린 listType 을 쓴다
                  const cardListType = item.listType
                  return (
                  <div
                    key={index}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleChartContainerClick(cardListType)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleChartContainerClick(cardListType) }}
                    className="flex-1 flex flex-col items-center gap-2.5 cursor-pointer rounded-lg hover:bg-gray-50 transition-colors p-2"
                    title="클릭하면 전체 목록을 사이드패널로 봅니다"
                  >
                    {/* Title */}
                    <h3
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '20px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6],
                      }}
                    >
                      {item.title}
                    </h3>

                    {/* Radial Chart */}
                    <div className="relative">
                      <Chart
                        type="radialBar"
                        series={[item.value]}
                        height={170}
                        options={{
                          chart: {
                            type: 'radialBar',
                            sparkline: {
                              enabled: true,
                            },
                          },
                          plotOptions: {
                            radialBar: {
                              hollow: {
                                size: '55%',
                              },
                              track: {
                                background: designTokens.colors.gray[1],
                                strokeWidth: '100%',
                              },
                              dataLabels: {
                                show: true,
                                name: {
                                  show: true,
                                  fontSize: '14px',
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontWeight: 600,
                                  color: designTokens.colors.gray[4],
                                  offsetY: -10,
                                },
                                value: {
                                  show: true,
                                  fontSize: '18px',
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontWeight: 400,
                                  color: designTokens.colors.gray[6],
                                  offsetY: 5,
                                  formatter: function(val: number) {
                                    return val + '%'
                                  },
                                },
                              },
                            },
                          },
                          colors: [item.color],
                          labels: [item.label],
                          stroke: {
                            lineCap: 'round',
                          },
                        }}
                      />
                    </div>

                    {/* Count Text */}
                    <div
                      className="text-center"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6],
                        lineHeight: 1.4,
                      }}
                    >
                      <div>
                        (전체) {item.isAmount ?
                        `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(item.total ?? 0)}원` :
                        `${(item.total ?? 0).toLocaleString()}개`
                      }
                      </div>
                      <div>
                        (완료) {item.isAmount ?
                        `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(item.completed ?? 0)}원` :
                        `${(item.completed ?? 0).toLocaleString()}개`
                      }
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48">
                <span style={{ color: designTokens.colors.gray[4] }}>
                  요약 정보가 없습니다.
                </span>
              </div>
            )}
          </div>
        </div>

      {/* 월별 트렌드 섹션 */}
      <>
        {/* Category Tab Selection - 왼쪽 정렬 */}
        <div className="mt-6 flex-shrink-0">
          <div className="flex justify-start">
            <div
              className="flex"
              style={{
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >

              {/* 업무 Tab */}
              <div
                className="flex items-center justify-center cursor-pointer transition-all duration-150"
                style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: selectedListType === 'work' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                  borderRight: `1px solid ${designTokens.colors.gray[3]}`,
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '18px',
                  fontWeight: 600,
                  color: selectedListType === 'work' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                }}
                onClick={() => handleListTypeChange('work')}
              >
                수행업무
              </div>

              {/* 과제 Tab */}
              {showTasks && (
              <div
                className="flex items-center justify-center cursor-pointer transition-all duration-150"
                style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: selectedListType === 'task' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                  borderRight: `1px solid ${designTokens.colors.gray[3]}`,
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '18px',
                  fontWeight: 600,
                  color: selectedListType === 'task' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                }}
                onClick={() => handleListTypeChange('task')}
              >
                세부실행과제
              </div>
              )}

              {/* 예산 Tab */}
              <div
                className="flex items-center justify-center cursor-pointer transition-all duration-150"
                style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: selectedListType === 'budget' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                  borderRight: `1px solid ${designTokens.colors.gray[3]}`,
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '18px',
                  fontWeight: 600,
                  color: selectedListType === 'budget' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                }}
                onClick={() => handleListTypeChange('budget')}
              >
                예산
              </div>

              {/* 성과 Tab */}
              <div
                className="flex items-center justify-center cursor-pointer transition-all duration-150"
                style={{
                  width: '120px',
                  height: '40px',
                  backgroundColor: selectedListType === 'performance' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '18px',
                  fontWeight: 600,
                  color: selectedListType === 'performance' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                }}
                onClick={() => handleListTypeChange('performance')}
              >
                성과
              </div>
            </div>
          </div>
        </div>

        {/* Chart Title Section */}
        <div className="mt-6 flex-shrink-0">
            <div
              className="flex items-center justify-between py-[10px]"
              style={{
                borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
              }}
            >
              {/* Chart Title */}
              <h2
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '20px',
                  lineHeight: '1.193359375em',
                  fontWeight: 700,
                  color: '#1B1B1B',
                }}
              >
                {getMonthlyTrendTitle()}
              </h2>

              {/* Detail Buttons */}
              <div className="flex items-center gap-2">
                <Button
                  size="small"
                  color="white"
                  className="flex items-center gap-1"
                  onClick={() => {
                    // Open department detail panel for overall department view with task type context
                    const taskTypeLabel = (() => {
                      switch (selectedListType) {
                        case 'task':
                          return '전체 세부실행과제'
                        case 'work':
                          return '전체 수행업무'
                        case 'budget':
                          return '전체 예산'
                        case 'performance':
                          return '전체 성과'
                        default:
                          return '전체 세부실행과제'
                      }
                    })()
                    handleOpenDepartmentDetail(taskTypeLabel)
                  }}
                >
                  전체 부서별 상세
                </Button>
                <Button
                  size="small"
                  color="white"
                  className="flex items-center gap-1"
                  onClick={() => {
                    // Open business detail panel for overall business view with task type context
                    const taskTypeLabel = (() => {
                      switch (selectedListType) {
                        case 'task':
                          return '전체 세부실행과제'
                        case 'work':
                          return '전체 수행업무'
                        case 'budget':
                          return '전체 예산'
                        case 'performance':
                          return '전체 성과'
                        default:
                          return '전체 세부실행과제'
                      }
                    })()
                    handleOpenBusinessDetail(taskTypeLabel)
                  }}
                >
                  전체 사업별 상세
                </Button>
              </div>
            </div>
          </div>

          {/* Monthly Charts based on selected list type — 월별 봉/포인트 또는 x축 월 라벨 클릭 시에만 해당 월 목록 오픈 */}
          {taskSummaryMonth && (
            <div className="mt-6 flex-shrink-0">
              <div
                className="bg-white rounded-lg border p-6"
                style={{ borderColor: designTokens.colors.gray[1] }}
                title="월별 봉 또는 아래 월 라벨을 클릭하면 해당 월 목록을 사이드패널로 봅니다"
              >
                {selectedListType === 'task' && (
                  (() => {
                    const chartData = transformMonthlyDataToChart(taskSummaryMonth.task.monthly)
                    monthlyTrendChartHandlerRef.current = buildChartClickHandler('task', chartData.monthKeys)
                    const chartOptions = getChartOptions(chartData.categories, 'task', chartData.series, monthlyTrendChartHandlerRef)

                    return (
                      <Chart
                        type="line"
                        series={chartData.series}
                        options={chartOptions}
                        height={400}
                      />
                    )
                  })()
                )}

                {selectedListType === 'work' && (
                  (() => {
                    const chartData = transformMonthlyTaskItemDataToChart(taskSummaryMonth.task_item.monthly)
                    monthlyTrendChartHandlerRef.current = buildChartClickHandler('work', chartData.monthKeys)
                    const chartOptions = getChartOptions(chartData.categories, 'work', chartData.series, monthlyTrendChartHandlerRef)

                    return (
                      <Chart
                        type="line"
                        series={chartData.series}
                        options={chartOptions}
                        height={400}
                      />
                    )
                  })()
                )}

                {selectedListType === 'budget' && (
                  (() => {
                    const chartData = transformMonthlyTaskBudgetDataToChart(taskSummaryMonth.task_budget.monthly)
                    monthlyTrendChartHandlerRef.current = buildChartClickHandler('budget', chartData.monthKeys)
                    const chartOptions = getChartOptions(chartData.categories, 'budget', chartData.series, monthlyTrendChartHandlerRef)

                    return (
                      <Chart
                        type="line"
                        series={chartData.series}
                        options={chartOptions}
                        height={400}
                      />
                    )
                  })()
                )}

                {selectedListType === 'performance' && (
                  (() => {
                    const chartData = transformMonthlyTaskPerformanceDataToChart(taskSummaryMonth.task_performance.monthly)
                    monthlyTrendChartHandlerRef.current = buildChartClickHandler('performance', chartData.monthKeys)
                    const chartOptions = getChartOptions(chartData.categories, 'performance', chartData.series, monthlyTrendChartHandlerRef)

                    return (
                      <Chart
                        type="line"
                        series={chartData.series}
                        options={chartOptions}
                        height={400}
                      />
                    )
                  })()
                )}
              </div>
            </div>
          )}

          {/* Department/Business Summary Section */}
          <div className="mt-6 flex-shrink-0">
            <div
              className="flex items-center justify-between gap-[10px] py-[10px]"
              style={{
                borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
              }}
            >
              {/* Left: Dynamic Title */}
              <div className="flex items-center gap-[18px]">
                <div className="flex items-center gap-[10px]">
                  <h2
                    style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '20px',
                      lineHeight: '1.193359375em',
                      fontWeight: 700,
                      color: '#1B1B1B',
                    }}
                  >
                    {getSummaryTitle()}
                  </h2>
                </div>
              </div>

              {/* Right: Tab Controls */}
              <div className="flex items-center gap-[10px]">
                {/* Department/Business Toggle */}
                <div
                  className="flex"
                  style={{
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  {/* 사업 Tab */}
                  <div
                    className="flex items-center justify-center cursor-pointer transition-all duration-150"
                    style={{
                      width: '80px',
                      height: '40px',
                      backgroundColor: summaryViewType === 'business' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '18px',
                      fontWeight: 600,
                      color: summaryViewType === 'business' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                    }}
                    onClick={() => handleSummaryViewTypeChange('business')}
                  >
                    사업
                  </div>
                  {/* 부서 Tab */}
                  <div
                    className="flex items-center justify-center cursor-pointer transition-all duration-150"
                    style={{
                      width: '80px',
                      height: '40px',
                      backgroundColor: summaryViewType === 'department' ? designTokens.colors.primary.dark : designTokens.colors.gray[1],
                      borderRight: `1px solid ${designTokens.colors.gray[3]}`,
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      fontSize: '18px',
                      fontWeight: 600,
                      color: summaryViewType === 'department' ? designTokens.colors.gray[1] : designTokens.colors.gray[4],
                    }}
                    onClick={() => handleSummaryViewTypeChange('department')}
                  >
                    부서
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Charts Content */}
            <div className="mt-6">
              <div className="bg-white rounded-lg border p-6" style={{ borderColor: designTokens.colors.gray[1] }}>
                {isDeptBusinessSummaryLoading ? (
                  <div className="flex items-center justify-center h-48">
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      color: designTokens.colors.gray[4]
                    }}>
                      {getSummaryTitle()} 데이터를 불러오는 중...
                    </span>
                  </div>
                ) : deptBusinessSummaryError ? (
                  <div className="flex items-center justify-center h-48">
                    <span style={{
                      fontFamily: designTokens.typography.fontFamily.sans[0],
                      color: designTokens.colors.error.DEFAULT
                    }}>
                      {deptBusinessSummaryError}
                    </span>
                  </div>
                ) : (
                  <div>
                    {(() => {
                      const currentSummary = summaryViewType === 'department' ? departmentSummary : businessSummary

                      if (!currentSummary || currentSummary.length === 0) {
                        return (
                          <div className="flex items-center justify-center h-48">
                            <span style={{
                              fontFamily: designTokens.typography.fontFamily.sans[0],
                              color: designTokens.colors.gray[4]
                            }}>
                              {getSummaryTitle()} 데이터가 없습니다.
                            </span>
                          </div>
                        )
                      }

                      const getLabelText = () => {
                        switch (selectedListType) {
                          case 'task': return '진행율'
                          case 'work': return '진행율'
                          case 'budget': return '집행율'
                          case 'performance': return '달성률'
                          default: return '진행율'
                        }
                      }

                      // Individual entity sections
                      return (
                        <div className="space-y-8">
                          {currentSummary.map((item: any, index: number) => {
                            const entityName = summaryViewType === 'department' ? item.department_name : item.business_name
                            const entityFilterOpts: { filterBusinessId?: string; filterDepartmentId?: string; entityName?: string } =
                              summaryViewType === 'department'
                                ? { filterDepartmentId: item.department_id, entityName }
                                : { filterBusinessId: item.business_id, entityName }
                            const entityKey = `${summaryViewType}-${item.department_id ?? item.business_id ?? index}-${selectedListType}`
                            const summaryData = (() => {
                              switch (selectedListType) {
                                case 'task':
                                  return item.task.total
                                case 'work':
                                  return item.task_item.total
                                case 'budget':
                                  return item.task_budget.total
                                case 'performance':
                                  return item.task_performance.total
                                default:
                                  return item.task.total
                              }
                            })()

                            const monthlyData = (() => {
                              switch (selectedListType) {
                                case 'task':
                                  return item.task.monthly
                                case 'work':
                                  return item.task_item.monthly
                                case 'budget':
                                  return item.task_budget.monthly
                                case 'performance':
                                  return item.task_performance.monthly
                                default:
                                  return item.task.monthly
                              }
                            })()

                            // Prepare individual monthly chart data (same format as main monthly charts)
                            const individualMonthlyChartData = (() => {
                              const sortedMonths = Object.keys(monthlyData || {}).sort()
                              const categories = sortedMonths.map(month => {
                                // Convert YYYY-MM to more readable format
                                const [year, monthNum] = month.split('-')
                                const totalCount = typeof monthlyData[month]?.total_count === 'number' ? monthlyData[month]?.total_count : 0
                                const completedCount = typeof monthlyData[month]?.completed_count === 'number' ? monthlyData[month]?.completed_count : 0

                                return `${year}년 ${parseInt(monthNum)}월 (${totalCount}/${completedCount})`
                              })
                              const monthKeys = sortedMonths

                              if (selectedListType === 'budget') {
                                // Budget: 전체, 집행, 집행률
                                const totalData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.total_target_figure || 0
                                })
                                const completedData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.total_done_figure || 0
                                })
                                const percentageData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return typeof monthData?.figure_percentage === 'number' ? monthData.figure_percentage : 0
                                })

                                return {
                                  categories,
                                  monthKeys,
                                  series: [
                                    {
                                      name: '전체',
                                      type: 'column',
                                      data: totalData
                                    },
                                    {
                                      name: '집행',
                                      type: 'column',
                                      data: completedData
                                    },
                                    {
                                      name: '집행률',
                                      type: 'line',
                                      data: percentageData
                                    }
                                  ]
                                }
                              } else if (selectedListType === 'performance') {
                                // Performance: 전체, 달성, 달성률
                                const totalData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.total_count || 0
                                })
                                const completedData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.completed_count || 0
                                })
                                const percentageData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return typeof monthData?.count_percentage === 'number' ? monthData.count_percentage : 0
                                })

                                return {
                                  categories,
                                  monthKeys,
                                  series: [
                                    {
                                      name: '전체',
                                      type: 'column',
                                      data: totalData
                                    },
                                    {
                                      name: '달성',
                                      type: 'column',
                                      data: completedData
                                    },
                                    {
                                      name: '달성률',
                                      type: 'line',
                                      data: percentageData
                                    }
                                  ]
                                }
                              } else {
                                // Task & Work: 전체, 완료, 완료율
                                const totalData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.total_count || 0
                                })
                                const completedData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return monthData?.completed_count || 0
                                })
                                const percentageData = sortedMonths.map(month => {
                                  const monthData = monthlyData?.[month]
                                  return typeof monthData?.count_percentage === 'number' ? monthData.count_percentage : 0
                                })

                                return {
                                  categories,
                                  monthKeys,
                                  series: [
                                    {
                                      name: '전체',
                                      type: 'column',
                                      data: totalData
                                    },
                                    {
                                      name: '완료',
                                      type: 'column',
                                      data: completedData
                                    },
                                    {
                                      name: '완료율',
                                      type: 'line',
                                      data: percentageData
                                    }
                                  ]
                                }
                              }
                            })()

                            const entityPercentage = (() => {
                              if (selectedListType === 'budget' || selectedListType === 'performance') {
                                return summaryData.figure_percentage || 0
                              } else {
                                return summaryData.count_percentage || 0
                              }
                            })()

                            const entityTotal = (() => {
                              if (selectedListType === 'budget' || selectedListType === 'performance') {
                                return summaryData.total_target_figure || 0
                              } else {
                                return summaryData.total_count || 0
                              }
                            })()

                            const entityCompleted = (() => {
                              if (selectedListType === 'budget' || selectedListType === 'performance') {
                                return summaryData.total_done_figure || 0
                              } else {
                                return summaryData.completed_count || 0
                              }
                            })()

                            return (
                              <div key={index} className="border-b pb-8" style={{ borderColor: designTokens.colors.gray[1] }}>
                                {/* Entity Title with Navigation Button */}
                                <div className="flex items-center justify-between mb-6">
                                  <h3
                                    style={{
                                      fontFamily: designTokens.typography.fontFamily.sans[0],
                                      fontSize: '20px',
                                      fontWeight: 700,
                                      color: designTokens.colors.gray[6],
                                    }}
                                  >
                                    {entityName}
                                  </h3>
                                  <Button
                                    size="small"
                                    color="white"
                                    onClick={() => {
                                      // Get task type label to append to entity name
                                      const taskTypeLabel = (() => {
                                        switch (selectedListType) {
                                          case 'task':
                                            return '세부실행과제'
                                          case 'work':
                                            return '수행업무'
                                          case 'budget':
                                            return '예산'
                                          case 'performance':
                                            return '성과'
                                          default:
                                            return '세부실행과제'
                                        }
                                      })()

                                      const entityNameWithType = `${entityName} ${taskTypeLabel}`

                                      if (summaryViewType === 'department') {
                                        // When viewing departments, open business detail with department ID
                                        const departmentId = summaryViewType === 'department' ? item.department_id : undefined
                                        handleOpenBusinessDetail(entityNameWithType, departmentId)
                                      } else {
                                        // When viewing businesses, open department detail with business ID
                                        const businessId = summaryViewType === 'business' ? item.business_id : undefined
                                        handleOpenDepartmentDetail(entityNameWithType, businessId)
                                      }
                                    }}
                                  >
                                    {summaryViewType === 'department' ? '사업별 상세' : '부서별 상세'}
                                  </Button>
                                </div>

                                {/* Charts Section */}
                                <div className="flex gap-6">
                                  {/* Left: Individual Circular Chart — 클릭 시 해당 엔티티 목록 패널 오픈 */}
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleChartContainerClick(selectedListType, entityFilterOpts)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleChartContainerClick(selectedListType, entityFilterOpts) }}
                                    className="flex-shrink-0 cursor-pointer rounded-lg hover:bg-gray-50 transition-colors p-2"
                                    style={{ width: '300px' }}
                                    title={`클릭하면 ${entityName} 목록을 사이드패널로 봅니다`}
                                  >
                                    <div className="text-center">
                                      <h4
                                        className="mb-4"
                                        style={{
                                          fontFamily: designTokens.typography.fontFamily.sans[0],
                                          fontSize: '16px',
                                          fontWeight: 600,
                                          color: designTokens.colors.gray[6],
                                        }}
                                      >
                                        {getSummaryTitle().split(' ')[2]} 현황
                                      </h4>
                                      <Chart
                                        type="radialBar"
                                        series={[entityPercentage]}
                                        height={200}
                                        options={{
                                          chart: {
                                            type: 'radialBar',
                                            sparkline: {
                                              enabled: true,
                                            },
                                          },
                                          plotOptions: {
                                            radialBar: {
                                              hollow: {
                                                size: '55%',
                                              },
                                              track: {
                                                background: designTokens.colors.gray[1],
                                                strokeWidth: '100%',
                                              },
                                              dataLabels: {
                                                show: true,
                                                name: {
                                                  show: true,
                                                  fontSize: '14px',
                                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                                  fontWeight: 600,
                                                  color: designTokens.colors.gray[4],
                                                  offsetY: -10,
                                                },
                                                value: {
                                                  show: true,
                                                  fontSize: '18px',
                                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                                  fontWeight: 400,
                                                  color: designTokens.colors.gray[6],
                                                  offsetY: 5,
                                                  formatter: function(val: number) {
                                                    return val.toFixed(1) + '%'
                                                  },
                                                },
                                              },
                                            },
                                          },
                                          colors: [
                                            index === 0 ? designTokens.colors.primary.dark :
                                            index === 1 ? designTokens.colors.secondary.dark :
                                            index === 2 ? designTokens.colors.warning :
                                            index === 3 ? designTokens.colors.success :
                                            designTokens.colors.error
                                          ],
                                          labels: [getLabelText()],
                                          stroke: {
                                            lineCap: 'round',
                                          },
                                        }}
                                      />
                                      <div
                                        className="text-center mt-2"
                                        style={{
                                          fontFamily: designTokens.typography.fontFamily.sans[0],
                                          fontSize: '14px',
                                          fontWeight: 600,
                                          color: designTokens.colors.gray[6],
                                          lineHeight: 1.4,
                                        }}
                                      >
                                        <div>
                                          전체: {selectedListType === 'budget' || selectedListType === 'performance' ?
                                            new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(entityTotal) + '원' :
                                            `${entityTotal}개`
                                          }
                                        </div>
                                        <div>
                                          완료: {selectedListType === 'budget' || selectedListType === 'performance' ?
                                            new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(entityCompleted) + '원' :
                                            `${entityCompleted}개`
                                          }
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Right: Individual Monthly Trend Chart — 월별 봉/포인트 또는 x축 월 라벨 클릭 시에만 해당 월 목록 오픈 */}
                                  <div
                                    className="flex-1 rounded-lg p-2"
                                    title={`월별 봉 또는 아래 월 라벨을 클릭하면 해당 월의 ${entityName} 목록을 사이드패널로 봅니다`}
                                  >
                                    <h4
                                      className="mb-4"
                                      style={{
                                        fontFamily: designTokens.typography.fontFamily.sans[0],
                                        fontSize: '16px',
                                        fontWeight: 600,
                                        color: designTokens.colors.gray[6],
                                      }}
                                    >
                                      {(() => {
                                        switch (selectedListType) {
                                          case 'task': return '전체 월별 세부실행과제 진행율'
                                          case 'work': return '전체 월별 수행업무 진행율'
                                          case 'budget': return '전체 월별 예산 집행율'
                                          case 'performance': return '전체 월별 성과 달성율'
                                          default: return '-'
                                        }
                                      })()}
                                    </h4>
                                    {(() => {
                                      const entityHandlerRef = getOrCreateEntityChartHandlerRef(entityKey)
                                      entityHandlerRef.current = buildChartClickHandler(
                                        selectedListType,
                                        individualMonthlyChartData.monthKeys,
                                        entityFilterOpts,
                                      )
                                      return (
                                        <Chart
                                          type="line"
                                          series={individualMonthlyChartData.series}
                                          height={250}
                                          options={getChartOptions(
                                            individualMonthlyChartData.categories,
                                            selectedListType,
                                            individualMonthlyChartData.series,
                                            entityHandlerRef,
                                          )}
                                        />
                                      )
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
      </>

      {/* Department Detail Side Panel */}
      <DepartmentDetailPanel
        isOpen={isDepartmentDetailPanelOpen}
        onClose={handleCloseDepartmentDetail}
        departmentName={selectedDepartmentName}
        selectedListType={selectedListType}
        businessId={selectedBusinessId}
      />

      {/* Business Detail Side Panel */}
      <BusinessDetailPanel
        isOpen={isBusinessDetailPanelOpen}
        onClose={handleCloseBusinessDetail}
        businessName={selectedBusinessName}
        selectedListType={selectedListType}
        departmentId={selectedDepartmentId}
      />

      {/* 요약 파이/월별 차트/부서·사업별 차트 클릭 → 항목별 리스트 사이드패널 (한 번에 하나만 열림).
          - 차트 봉/포인트를 정확히 클릭한 경우: 해당 월 범위(dateRangeOverride)로 좁힘
          - 부서/사업별 차트 클릭: 그 엔티티(filterBusinessId/filterDepartmentId)로 좁힘 */}
      {(() => {
        const override = summaryListPanel?.dateRangeOverride
        const panelStartDate = override ? override.start : null
        const panelEndDate = override ? override.end : null
        const commonProps = {
          onClose: closeSummaryListPanel,
          workspaceId: selectedWorkspace?.id || '',
          selectedBusinessId: summaryListPanel?.filterBusinessId,
          selectedBusinessName: summaryListPanel?.filterBusinessName,
          selectedDepartmentId: summaryListPanel?.filterDepartmentId,
          selectedDepartmentName: summaryListPanel?.filterDepartmentName,
          startDate: panelStartDate,
          endDate: panelEndDate,
        }
        return (
          <>
            <TaskItemsListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task_item'}
              title={summaryListPanel?.type === 'task_item' ? summaryListPanel.title : '수행업무 목록'}
              onTaskItemClick={handleTaskItemRowClick}
            />
            <TasksListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'task'}
              title={summaryListPanel?.type === 'task' ? summaryListPanel.title : '세부실행과제 목록'}
              onTaskClick={handleTaskRowClick}
              onReportClick={handleReportClick}
              isActionButtonClicked={isActionButtonClicked}
              refreshTrigger={tasksListRefreshTrigger}
            />
            <TaskBudgetsListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'budget'}
              title={summaryListPanel?.type === 'budget' ? summaryListPanel.title : '예산 목록'}
              onBudgetItemClick={handleBudgetItemClick}
            />
            <TaskPerformancesListPanel
              {...commonProps}
              isOpen={summaryListPanel?.type === 'performance'}
              title={summaryListPanel?.type === 'performance' ? summaryListPanel.title : '성과 목록'}
              onPerformanceItemClick={handlePerformanceItemClick}
            />
          </>
        )
      })()}

      {/* 목록 사이드패널의 행 클릭 → 항목 상세 사이드패널 (z-index가 list 패널보다 위) */}
      <TaskDetailPanel
        isOpen={isTaskDetailPanelOpen}
        onClose={() => { setIsTaskDetailPanelOpen(false); setSelectedTaskIdForDetail(null) }}
        taskId={selectedTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isTaskItemDetailPanelOpen}
        onClose={() => { setIsTaskItemDetailPanelOpen(false); setSelectedTaskItemIdForDetail(null) }}
        taskId={selectedTaskItemIdForDetail}
        taskType="task_item"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskItemEdit}
      />
      <TaskDetailPanel
        isOpen={isBudgetDetailPanelOpen}
        onClose={() => { setIsBudgetDetailPanelOpen(false); setSelectedBudgetTaskIdForDetail(null) }}
        taskId={selectedBudgetTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isPerformanceDetailPanelOpen}
        onClose={() => { setIsPerformanceDetailPanelOpen(false); setSelectedPerformanceTaskIdForDetail(null) }}
        taskId={selectedPerformanceTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />

      {/* Report Detail Panel — id/templateKey만 전달, 패널이 자체 fetch */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={selectedWorkspace?.id || ''}
        onReportTypeChange={handleReportTemplateChange}
        onTaskClick={handleReportTaskDetail}
        onEditTask={handleReportTaskEdit}
      />

      {/* 세부실행과제 수정 패널 — 보고서 패널의 "세부실행과제 수정" 클릭 시 */}
      {reportTaskEditPanel}

      {/* 수행업무 수정 패널 — 상세 패널의 "수정" 클릭 시 (상세 패널은 열린 상태 유지) */}
      {editTaskItemData && (
        <TaskItemCreatePanel
          isOpen={isTaskItemEditPanelOpen}
          onClose={handleTaskItemEditPanelClose}
          taskId={editTaskItemData.taskId}
          taskName={editTaskItemData.taskName}
          taskSubgroupName={editTaskItemData.taskSubgroupName}
          businessName={editTaskItemData.businessName}
          onSuccess={handleTaskItemEditSuccess}
          editMode={true}
          taskItemId={editTaskItemData.id}
          initialTaskItemData={{
            name: editTaskItemData.name,
            startAt: editTaskItemData.startAt,
            endAt: editTaskItemData.endAt,
            output: editTaskItemData.output
          }}
        />
      )}

      {/* 세부실행과제 수정 패널 — budget/performance 초기값은 taskOriginal에서 추출 */}
      {editTaskData && (() => {
        const taskOriginal = editTaskData.taskOriginal
        return (
          <TaskCreatePanel
            isOpen={isTaskEditPanelOpen}
            onClose={handleTaskEditPanelClose}
            taskSubgroupId={editTaskData.taskSubgroupId}
            taskSubgroupName={editTaskData.taskSubgroupName}
            taskGroupName={editTaskData.taskGroupName}
            businessName={editTaskData.businessName}
            businessId={editTaskData.businessId}
            onSuccess={handleTaskEditSuccess}
            editMode={true}
            taskId={editTaskData.id}
            initialTaskData={{
              name: editTaskData.name,
              assigneeId: editTaskData.assigneeId,
              assigneeName: editTaskData.assigneeName,
              subAssigneeId: editTaskData.subAssigneeId,
              subAssigneeName: editTaskData.subAssigneeName,
              managerId: editTaskData.managerId,
              managerName: editTaskData.managerName,
              reviewerId: editTaskData.reviewerId,
              reviewerName: editTaskData.reviewerName,
              departmentId: editTaskData.departmentId,
              departmentName: editTaskData.departmentName,
              startAt: editTaskData.startAt,
              endAt: editTaskData.endAt,
              description: editTaskData.description,
              budget: (taskOriginal as any)?.budget ? {
                id: (taskOriginal as any).budget.id || '',
                resultDescription: (taskOriginal as any).budget.result_description || '',
                items: ((taskOriginal as any).budget.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              performance: (taskOriginal as any)?.performance ? {
                id: (taskOriginal as any).performance.id || '',
                name: (taskOriginal as any).performance.name || '',
                unit: (taskOriginal as any).performance.unit || '',
                items: ((taskOriginal as any).performance.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              labels: editTaskData.labels,
              detailInfos: editTaskData.detailInfos,
              extraInfos: editTaskData.extraInfos,
              judgmentBasis: editTaskData.judgmentBasis
            }}
          />
        )
      })()}
    </div>
  )
}